import { useRef } from 'react';

import { Box, Flex } from '@joinrepublic/dls-ui-kit';

import {
  HIDE_SCROLLBAR,
  HOVER_TRANSITION,
} from '@r-client/republic/ui/offerings';

import { ALL_SECTORS_LABEL, ALL_SECTORS_SLUG } from '../constants';
import { ScrollArrowButton } from '../scroll-arrow-button/scroll-arrow-button';
import { SectorChip } from '../sector-chip/sector-chip';
import { SCROLL_STEP_PX, SECTOR_GAP, SECTOR_ROW_PADDING_Y } from './constants';
import { ISectorChipsRowProps } from './types';
import { useHorizontalScrollEdges } from './use-horizontal-scroll-edges';

/**
 * Horizontally scrollable sector filter, the desktop face of `SectorFilter` —
 * below `md` the same selection is offered as `SectorMenu` instead. The arrows
 * are overlays rather than inline siblings so that showing or hiding one never
 * changes the scroller's width — that width is the input to their own
 * visibility condition, and an inline button would oscillate against it at the
 * far edge.
 */
export const SectorChipsRow = ({
  sectors,
  selectedSlug,
  onSelect,
}: ISectorChipsRowProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { canScrollLeft, canScrollRight, onScroll } = useHorizontalScrollEdges(
    scrollerRef,
    sectors.length
  );

  const scrollByStep = (direction: -1 | 1) => {
    scrollerRef.current?.scrollBy({
      left: direction * SCROLL_STEP_PX,
      behavior: 'smooth',
    });
  };

  return (
    <Box position="relative">
      <Flex
        ref={scrollerRef}
        onScroll={onScroll}
        // `group` is the ARIA role for a set of controls, which is what the
        // chips are. It is also Chakra's `_groupHover` anchor, and Chakra v2 has
        // no named groups: a chip using `_groupHover` would light up on hover
        // anywhere in this row. Chips must scope their own hover styles.
        role="group"
        aria-label="Sectors"
        align="center"
        gap={SECTOR_GAP}
        py={SECTOR_ROW_PADDING_Y}
        overflowX="auto"
        sx={HIDE_SCROLLBAR}
      >
        <SectorChip
          label={ALL_SECTORS_LABEL}
          slug={ALL_SECTORS_SLUG}
          isSelected={selectedSlug === null}
          onSelect={() => onSelect(null)}
          // Fades out as the row scrolls, leaving its slot to the back arrow.
          // `opacity` rather than `display`, because removing the chip from the
          // flow would rewrite `scrollLeft` and jump the row mid-scroll.
          opacity={canScrollLeft ? 0 : 1}
          pointerEvents={canScrollLeft ? 'none' : 'auto'}
          aria-hidden={canScrollLeft || undefined}
          tabIndex={canScrollLeft ? -1 : undefined}
          transition={`opacity ${HOVER_TRANSITION}`}
        />

        {sectors.map((sector) => (
          <SectorChip
            key={sector.slug}
            label={sector.displayName}
            slug={sector.slug}
            isSelected={selectedSlug === sector.slug}
            onSelect={() => onSelect(sector.slug)}
          />
        ))}
      </Flex>

      {canScrollLeft && (
        <ScrollArrowButton direction="left" onClick={() => scrollByStep(-1)} />
      )}
      {canScrollRight && (
        <ScrollArrowButton direction="right" onClick={() => scrollByStep(1)} />
      )}
    </Box>
  );
};
